import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

// Initialize Sentry for error tracking
export const initSentry = () => {
  // Only initialize in production or if SENTRY_DSN is provided
  const isDev = import.meta.env.DEV;
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (!isDev && sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 0.1, // Capture 10% of transactions
      // Session Replay
      replaysSessionSampleRate: 0.1, // Sample 10% of sessions
      replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
      
      beforeSend(event, hint) {
        // Log to database as well
        logErrorToDatabase(event, hint);
        return event;
      },
    });
  } else if (isDev) {
    console.log('[Sentry] Running in development mode - errors logged to console and database only');
  }
};

// Log errors to database for custom tracking
async function logErrorToDatabase(event: Sentry.Event, hint?: Sentry.EventHint) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    const error = hint?.originalException;
    const errorMessage = event.message || (error instanceof Error ? error.message : 'Unknown error');
    const errorStack = event.exception?.values?.[0]?.stacktrace?.frames
      ? JSON.stringify(event.exception.values[0].stacktrace.frames)
      : (error instanceof Error ? error.stack : undefined);

    await supabase.from('error_logs').insert([{
      user_id: user?.id || null,
      error_message: errorMessage,
      error_stack: errorStack || null,
      error_info: JSON.parse(JSON.stringify({
        level: event.level,
        tags: event.tags || {},
        extra: event.extra || {},
        contexts: event.contexts || {},
      })),
      url: window.location.href,
      user_agent: navigator.userAgent,
      severity: event.level || 'error',
    }]);
  } catch (err) {
    console.error('[Error Logging] Failed to log error to database:', err);
  }
}

// Manual error capture
export const captureError = (error: Error, context?: Record<string, any>) => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error, { extra: context });
  }
  
  // Always log to database
  logErrorToDatabase(
    { message: error.message, level: 'error' } as Sentry.Event,
    { originalException: error }
  );
  
  console.error('[Error]', error, context);
};

// Set user context for error tracking
export const setUserContext = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
      });
    }
  } catch (error) {
    console.error('[Sentry] Failed to set user context:', error);
  }
};

// Clear user context on logout
export const clearUserContext = () => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser(null);
  }
};
